import {PermissionEnum} from '../enums/permission-enum';
import {ApiParamConstants} from '../utils/api-constants';
import {AppConstants} from '../utils/app-constants';
import {EntityStatusEnum} from '../enums/entity-status.enum';
import {MonthEnum} from '../enums/month.enum';
import {ProductStatusEnum} from '../enums/product-status.enum';

export class ListHttpParameters {
  permission: PermissionEnum;
  objectName: string;
  objectId: string;
  page = 0;
  size: number = AppConstants.LIST_PAGE_SIZE;
  sort: any;
  keyword: string;
  countryReg: string;
  activated: boolean | EntityStatusEnum[] = null;
  type: string;
  status: any;
  directlyLinkedOnly: boolean = null;
  canRequestUid: boolean = null;
  blocked: boolean = null;
  strictSearch: boolean = null;
  reliable: boolean = null;
  canOrderUid: boolean = null;
  orderType: any = null;
  intendedMarket: any = null;
  orderProductType: any = null;
  orderPhysicalDelivery: any = null;
  searchByKeyword: boolean;
  eoid: string;
  fid: string;
  mid: string;
  overdueTargetDate: boolean = null;
  noCancelation: boolean = null;
  fuzzyDisabled: boolean = null;
  regEoid: string;
  productTypeId: string;
  productsApproved: boolean = null;
  approved: boolean = null;
  productStatus: ProductStatusEnum = null;
  comment: string;
  exciseWarehouse: boolean = null;
  productId: string;
  month: MonthEnum = null;
  domainId: number | string = null; 
  year: number = null;
  eoApproved: boolean = null;
  authorityNotApproved: boolean = null;
  lateValidation: boolean = null;
  eosDomainId: number = null;
  disabled: boolean = null;
  isPageable: boolean = null;
  isDashboard: boolean = false;
  omitPagination: boolean = false;
  productDomainId?: string;
  productDomainName?: string;
  createdDate?: string;
  updatedDate?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  updatedDateFrom?: string;
  updatedDateTo?: string;
  eoId?: string;
  eoIds?: string;
  productIds?: string;
  facilityIds?: string;
  facilityId?: string = null;
  machineId?: string | number | null;
  taxLevelId?: string | null;
  country?: string | null;
  
  // CHANGED: Multi-select filters as comma-separated strings
  // These will be populated from arrays in the component
  statuses: string;         
  domainIds: string;       
  productTypeIds: string;   

  constructor(permission?: PermissionEnum, objectName?: string, objectId?: string) {
    this.permission = permission;
    this.objectName = objectName;
    this.objectId = objectId;
  }

  getHttpParams(): { [param: string]: string | string[] } {
    const params = {};

    this.permission && (params[ApiParamConstants.PERMISSION] = this.permission);
    this.objectId && this.objectName && (params[this.objectName] = encodeURIComponent(this.objectId));
    
    if (this.omitPagination !== true) {
      if (this.isDashboard === true) {
        // For dashboard, use large size to get all data if not omitted
        params[ApiParamConstants.LIST_PAGE] = 0;
        params[ApiParamConstants.LIST_PAGE_SIZE] = 2000;
      } else {
        (this.page || this.page === 0) && (params[ApiParamConstants.LIST_PAGE] = this.page.toString());
        this.size && (params[ApiParamConstants.LIST_PAGE_SIZE] = this.size.toString());
      }
    }

    this.sort && (params[ApiParamConstants.LIST_SORT] = this.sort);
    this.keyword && (params[ApiParamConstants.LIST_KEYWORD] = encodeURIComponent(this.keyword));
    this.countryReg && (params[ApiParamConstants.LIST_COUNTRIES] = this.countryReg);
    
    if (this.activated != null) {
      if (typeof this.activated === 'boolean') {
        this.activated != null && (params[ApiParamConstants.LIST_ACTIVATED] = this.activated.toString());
      } else {
        this.activated != null && (params[ApiParamConstants.LIST_ENTITY_STATUS] = this.activated.toString());
      }
    }
    
    // Multi-select statuses
    this.statuses && (params[ApiParamConstants.LIST_STATUS] = this.statuses);
    
    // Original mappings (without conditional logic)
    this.domainId != null && (params[ApiParamConstants.PRODUCT_TYPES_DOMAIN_ID] = this.domainId); 
    this.productDomainId && (params[ApiParamConstants.PRODUCT_TYPES_DOMAIN_ID] = this.productDomainId); 
    this.domainIds && (params[ApiParamConstants.PRODUCT_DOMAIN_IDS] = this.domainIds); // Multi-select domain IDs


    // Reverted conditional logic for productTypeId / productTypeIds
    this.productTypeIds && (params[ApiParamConstants.LIST_PRODUCT_TYPE_IDS] = this.productTypeIds);
    this.productTypeId && (params[ApiParamConstants.LIST_PRODUCT_TYPE_ID] = this.productTypeId); 
    
    this.type && (params[ApiParamConstants.LIST_TYPE] = this.type);
    this.status && (params[ApiParamConstants.LIST_STATUS] = this.status);
    this.directlyLinkedOnly && (params[ApiParamConstants.LIST_DIRECTLY_LINKED_ONLY] = this.directlyLinkedOnly.toString());
    this.canRequestUid != null && (params[ApiParamConstants.LIST_CAN_REQUEST_UID] = this.canRequestUid.toString());
    this.blocked != null && (params[ApiParamConstants.LIST_BLOCKED] = this.blocked.toString());
    this.strictSearch != null && (params[ApiParamConstants.LIST_STRICT_SEARCH] = this.strictSearch.toString());
    this.reliable != null && (params[ApiParamConstants.LIST_EO_RELIABLE] = this.reliable.toString());
    this.canOrderUid != null && (params[ApiParamConstants.LIST_EO_CAN_ORDER_UID] = this.canOrderUid.toString());
    this.orderType != null && (params[ApiParamConstants.LIST_ORDER_TYPE] = this.orderType.toString());
    this.intendedMarket != null && (params[ApiParamConstants.LIST_ORDER_INTENDED_MARKET] = this.intendedMarket.toString());
    this.orderProductType != null && (params[ApiParamConstants.LIST_ORDER_PRODUCT_TYPE] = this.orderProductType.toString());
    this.orderPhysicalDelivery != null && (params[ApiParamConstants.LIST_ORDER_PHYSICAL_DELIVERY] = this.orderPhysicalDelivery.toString());
    
    // BACKEND RECORD MATCHING
    this.eoIds && (params['eoIds'] = this.eoIds);
    this.facilityIds && (params['facilityIds'] = this.facilityIds);
    this.machineId && (params['machineId'] = this.machineId.toString());
    this.productIds && (params['productIds'] = this.productIds);

    // Backward compatibility or legacy support if still needed by other parts of the app
    this.eoid && (params[ApiParamConstants.REPORTING_EOID] = this.eoid);
    this.fid && (params[ApiParamConstants.REPORTING_FID] = this.fid);
    this.mid && (params[ApiParamConstants.REPORTING_MID] = this.mid);
    this.productId && (params[ApiParamConstants.PRODUCT_ID] = this.productId);
    
    this.overdueTargetDate != null && (params[ApiParamConstants.LIST_ORDER_OVERDUE_TARGET_DATE] = this.overdueTargetDate);
    this.noCancelation != null && (params[ApiParamConstants.LIST_ORDER_NO_CANCELATION] = this.noCancelation);
    this.fuzzyDisabled != null && (params[ApiParamConstants.LIST_FUZZY_DISABLED] = this.fuzzyDisabled);
    this.regEoid && (params[ApiParamConstants.LIST_EO_REG_EOID] = this.regEoid);
    // this.productTypeId && (params[ApiParamConstants.LIST_PRODUCT_TYPE_ID] = this.productTypeId); // This is now handled above
    this.productDomainName && (params['productDomainName'] = this.productDomainName);
    this.taxLevelId && (params['taxLevelId'] = this.taxLevelId);
    this.productsApproved != null && (params[ApiParamConstants.LIST_ECONOMIC_OPERATORS_PRODUCTS_APPROVED] = this.productsApproved.toString());
    this.approved != null && (params[ApiParamConstants.PRODUCT_APPROVED] = this.approved);
    this.productStatus != null && (params[ApiParamConstants.PRODUCT_STATUS] = this.productStatus);
    this.comment != null && (params[ApiParamConstants.PRODUCT_COMMENT] = this.comment);
    this.exciseWarehouse != null && (params[ApiParamConstants.FACILITY_EXCISE_WAREHOUSE] = this.exciseWarehouse);
    this.month != null && (params[ApiParamConstants.PRODUCTION_REQUEST_MONTH] = this.month);
    this.year != null && (params[ApiParamConstants.REPORTING_YEAR] = this.year);
    this.eoApproved != null && (params[ApiParamConstants.EXCISE_WAREHOUSE_EO_APPROVED] = this.eoApproved);
    this.authorityNotApproved != null && (params[ApiParamConstants.EXCISE_WAREHOUSE_AUTHORITY_NOT_APPROVED] = this.authorityNotApproved);
    this.lateValidation != null && (params[ApiParamConstants.EXCISE_WAREHOUSE_LATE_VALIDATION] = this.lateValidation);
    this.eosDomainId != null && (params[ApiParamConstants.EOS_DOMAIN_ID] = this.eosDomainId);
    this.isPageable != null && (params[ApiParamConstants.IS_PAGEABLE] = this.isPageable);
    this.createdDateFrom && (params['createdDateFrom'] = this.createdDateFrom);
    this.createdDateTo && (params['createdDateTo'] = this.createdDateTo);
    this.updatedDateFrom && (params['updatedDateFrom'] = this.updatedDateFrom);
    this.updatedDateTo && (params['updatedDateTo'] = this.updatedDateTo);
    this.createdDate && (params['createdDate'] = this.createdDate);
    this.updatedDate && (params['updatedDate'] = this.updatedDate);
    this.eoId && (params['eoId'] = this.eoId);
    // Multi-select product IDs
    // this.productIds && (params[ApiParamConstants.PRODUCT_IDS] = this.productIds); // Handled above
    this.facilityId  && (params['facilityId']  = this.facilityId);
    this.country && (params['country'] = this.country);
    
    return params;
  }
}

export namespace ListHttpParameters {
  export function createSearchParams(status: EntityStatusEnum[] | boolean, permission?: PermissionEnum, objectName?: string, objectId?: string): ListHttpParameters {
    const params = new ListHttpParameters(permission, objectName, objectId);
    params.activated = status;
    params.strictSearch = true;
    return params;
  }

  export function createEoSearchParams(
    status: EntityStatusEnum[] | boolean, permission?: PermissionEnum, objectName?: string, objectId?: string): ListHttpParameters {
    const params = createSearchParams(status, permission, objectName, objectId);
    params.canRequestUid = true; 
    params.activated = status;
    params.strictSearch = true;
    return params;
  }
}